import { useEffect, useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { Activity, Bell, BellOff, FileText, TrendingUp, Users, AlertTriangle, Clock, Send, CheckCircle2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { KpiCard } from '@/components/KpiCard';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { Paywall } from '@/components/Paywall';
import { useSubscription } from '@/hooks/useSubscription';

interface RepRow {
  user_id: string;
  full_name: string;
  email: string;
  last_event_at: string | null;
  events_7d: number;
  deals_7d: number;
  push_enabled: boolean;
  daily_activity: number[]; // last 14 days, oldest -> newest
}

interface FunnelTotals {
  invited: number;
  signed_in: number;
  first_deal: number;
  first_activity: number;
  first_next_step: number;
  push_on: number;
}

const DAY = 86_400_000;

export default function PilotHealth() {
  const { mode, role, isSolo, dealershipId, loading: authLoading } = useAuth();
  const { hasAccess, loading: subLoading } = useSubscription();
  const [reps, setReps] = useState<RepRow[]>([]);
  const [totals, setTotals] = useState({
    wau: 0,
    dau: 0,
    deals_7d: 0,
    activities_7d: 0,
    push_pct: 0,
    overdue_next_step: 0,
  });
  const [funnel, setFunnel] = useState<FunnelTotals>({
    invited: 0, signed_in: 0, first_deal: 0, first_activity: 0, first_next_step: 0, push_on: 0,
  });
  const [loading, setLoading] = useState(true);

  const isAllowed = mode === 'real' && (role === 'manager' || role === 'admin') && !isSolo;

  useEffect(() => {
    if (!isAllowed || !dealershipId) {
      setLoading(false);
      return;
    }
    let active = true;
    (async () => {
      setLoading(true);
      const since7 = new Date(Date.now() - 7 * DAY).toISOString();
      const since1 = new Date(Date.now() - DAY).toISOString();
      const since14 = new Date(Date.now() - 14 * DAY).toISOString();

      const [profilesRes, rolesRes, eventsRes, dealsRes, activitiesRes, subsRes, onboardingRes, activities14Res] = await Promise.all([
        supabase.from('profiles').select('id, full_name, email').eq('dealership_id', dealershipId),
        supabase.from('user_roles').select('user_id, role').eq('dealership_id', dealershipId),
        supabase.from('usage_events').select('user_id, event_name, created_at').eq('dealership_id', dealershipId).gte('created_at', since7),
        supabase.from('deals').select('id, rep_id, next_step, stage, created_at, last_activity_at').eq('dealership_id', dealershipId),
        supabase.from('activities').select('id, rep_id, created_at').eq('dealership_id', dealershipId).gte('created_at', since7),
        supabase.from('push_subscriptions').select('user_id').eq('dealership_id', dealershipId),
        supabase.from('user_onboarding').select('user_id, created_first_deal_at, logged_first_activity_at, set_first_next_step_at, enabled_notifications_at'),
        supabase.from('activities').select('rep_id, created_at').eq('dealership_id', dealershipId).gte('created_at', since14),
      ]);

      if (!active) return;

      const profiles = profilesRes.data ?? [];
      const roleMap = new Map<string, string>();
      (rolesRes.data ?? []).forEach(r => {
        // Prefer manager > rep
        const prev = roleMap.get(r.user_id);
        if (!prev || r.role === 'manager' || r.role === 'admin') roleMap.set(r.user_id, r.role);
      });
      // Only include reps in the per-rep table (managers tracked but listed at bottom too)
      const events = eventsRes.data ?? [];
      const subs = new Set((subsRes.data ?? []).map(s => s.user_id));
      const deals = dealsRes.data ?? [];
      const activities = activitiesRes.data ?? [];
      const activities14 = activities14Res.data ?? [];
      const onboarding = onboardingRes.data ?? [];

      const eventsByUser = new Map<string, { count: number; last: string }>();
      const activeUserIdsLast24 = new Set<string>();
      events.forEach(e => {
        const cur = eventsByUser.get(e.user_id) ?? { count: 0, last: '' };
        cur.count += 1;
        if (!cur.last || e.created_at > cur.last) cur.last = e.created_at;
        eventsByUser.set(e.user_id, cur);
        if (e.created_at >= since1) activeUserIdsLast24.add(e.user_id);
      });

      const dealsByRep7d = new Map<string, number>();
      deals.forEach(d => {
        if (!d.rep_id || d.created_at < since7) return;
        dealsByRep7d.set(d.rep_id, (dealsByRep7d.get(d.rep_id) ?? 0) + 1);
      });

      // Per-rep daily activity for the last 14 days
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const dayBuckets = (rep: string): number[] => {
        const buckets = new Array(14).fill(0);
        activities14.forEach(a => {
          if (a.rep_id !== rep) return;
          const idx = 13 - Math.floor((today.getTime() - new Date(a.created_at).getTime()) / DAY);
          if (idx >= 0 && idx < 14) buckets[idx] += 1;
        });
        return buckets;
      };

      const rows: RepRow[] = profiles.map(p => {
        const ev = eventsByUser.get(p.id);
        return {
          user_id: p.id,
          full_name: p.full_name || p.email,
          email: p.email,
          last_event_at: ev?.last ?? null,
          events_7d: ev?.count ?? 0,
          deals_7d: dealsByRep7d.get(p.id) ?? 0,
          push_enabled: subs.has(p.id),
          daily_activity: dayBuckets(p.id),
        };
      }).sort((a, b) => b.events_7d - a.events_7d);

      const overdueNoNext = deals.filter(d => d.stage !== 'sold' && (!d.next_step || !d.next_step.trim())).length;
      const pushPct = profiles.length === 0 ? 0 : Math.round((rows.filter(r => r.push_enabled).length / profiles.length) * 100);

      // Activation funnel — restrict to people in this dealership.
      const memberIds = new Set(profiles.map(p => p.id));
      const memberOnboarding = onboarding.filter((o: any) => memberIds.has(o.user_id));
      setFunnel({
        invited: profiles.length,
        signed_in: profiles.length, // every profile row implies they signed in at least once
        first_deal: memberOnboarding.filter((o: any) => o.created_first_deal_at).length,
        first_activity: memberOnboarding.filter((o: any) => o.logged_first_activity_at).length,
        first_next_step: memberOnboarding.filter((o: any) => o.set_first_next_step_at).length,
        push_on: memberOnboarding.filter((o: any) => o.enabled_notifications_at).length,
      });

      setReps(rows);
      setTotals({
        wau: new Set(events.map(e => e.user_id)).size,
        dau: activeUserIdsLast24.size,
        deals_7d: deals.filter(d => d.created_at >= since7).length,
        activities_7d: activities.length,
        push_pct: pushPct,
        overdue_next_step: overdueNoNext,
      });
      setLoading(false);
    })();
    return () => { active = false; };
  }, [isAllowed, dealershipId]);

  const stalledReps = useMemo(
    () => reps.filter(r => !r.last_event_at || Date.now() - new Date(r.last_event_at).getTime() > 2 * DAY),
    [reps],
  );

  const nudge = (rep: RepRow) => {
    const subject = encodeURIComponent('Quick check-in on PullOver');
    const body = encodeURIComponent(
      `Hey ${rep.full_name.split(' ')[0]},\n\n` +
      `Noticed you haven't logged an activity in PullOver for a couple of days. ` +
      `Anything blocking you? Even a quick "phone call" log keeps your streak alive.\n\n` +
      `Thanks,\nManager`,
    );
    window.location.href = `mailto:${rep.email}?subject=${subject}&body=${body}`;
  };

  if (authLoading) return null;
  if (!isAllowed) return <Navigate to="/dashboard" replace />;

  if (!subLoading && !hasAccess) {
    return (
      <div className="space-y-6 pb-20 lg:pb-0 max-w-2xl">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Activity className="h-6 w-6 text-primary" /> Pilot Health
          </h1>
          <p className="text-sm text-muted-foreground">Manager-only dealership health metrics.</p>
        </div>
        <Paywall
          feature="Pilot Health"
          description="DAU/WAU, per-rep usage, and stale-rep alerts. Available on Solo and Dealership plans."
        ><div /></Paywall>
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-20 lg:pb-0">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Activity className="h-6 w-6 text-primary" /> Pilot Health
        </h1>
        <p className="text-sm text-muted-foreground">Are reps actually using PullOver? Last 7 days.</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-6 gap-3">
        <KpiCard label="DAU" value={totals.dau} icon={Users} trend="last 24h" />
        <KpiCard label="WAU" value={totals.wau} icon={Users} trend="last 7d" />
        <KpiCard label="Deals (7d)" value={totals.deals_7d} icon={FileText} />
        <KpiCard label="Activities (7d)" value={totals.activities_7d} icon={TrendingUp} />
        <KpiCard label="Push opt-in" value={`${totals.push_pct}%`} icon={Bell} />
        <KpiCard label="No next step" value={totals.overdue_next_step} icon={AlertTriangle} />
      </div>

      {/* Activation funnel */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <CheckCircle2 className="h-5 w-5 text-primary" /> Activation funnel
          </CardTitle>
          <CardDescription>How far each invited rep has gotten in setup.</CardDescription>
        </CardHeader>
        <CardContent>
          <FunnelBars funnel={funnel} />
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg">Per-rep usage</CardTitle>
          <CardDescription>Sorted by activity. Red flag: no app event in 48h.</CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : reps.length === 0 ? (
            <p className="text-sm text-muted-foreground">No team members yet. Invite reps from Settings.</p>
          ) : (
            <div className="space-y-2">
              {reps.map(r => {
                const stale = !r.last_event_at || Date.now() - new Date(r.last_event_at).getTime() > 2 * DAY;
                return (
                  <div key={r.user_id} className="flex flex-col gap-2 p-3 rounded-lg border border-border sm:flex-row sm:items-center sm:justify-between">
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate flex items-center gap-2">
                        {r.full_name}
                        {stale && <Badge variant="destructive" className="text-[10px]">Stale</Badge>}
                      </p>
                      <p className="text-xs text-muted-foreground truncate">{r.email}</p>
                      <Heatmap days={r.daily_activity} />
                    </div>
                    <div className="flex items-center gap-4 shrink-0 text-xs">
                      <div className="text-center">
                        <p className="font-semibold text-foreground">{r.events_7d}</p>
                        <p className="text-muted-foreground">events</p>
                      </div>
                      <div className="text-center">
                        <p className="font-semibold text-foreground">{r.deals_7d}</p>
                        <p className="text-muted-foreground">deals</p>
                      </div>
                      <div className="text-center w-16">
                        {r.push_enabled
                          ? <Bell className="h-4 w-4 text-primary mx-auto" />
                          : <BellOff className="h-4 w-4 text-muted-foreground mx-auto" />}
                        <p className="text-muted-foreground mt-0.5">push</p>
                      </div>
                      <div className="text-right w-24 text-muted-foreground flex items-center gap-1 justify-end">
                        <Clock className="h-3 w-3" />
                        {r.last_event_at ? timeAgoShort(r.last_event_at) : 'Never'}
                      </div>
                      {stale && (
                        <Button size="sm" variant="outline" className="gap-1.5" onClick={() => nudge(r)}>
                          <Send className="h-3 w-3" /> Nudge
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {stalledReps.length > 0 && (
        <Card className="border-yellow-500/20">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-yellow-400" /> Reps to nudge
            </CardTitle>
            <CardDescription>No app activity in 48h+. Consider a quick check-in.</CardDescription>
          </CardHeader>
          <CardContent>
            <ul className="text-sm space-y-1">
              {stalledReps.map(r => (
                <li key={r.user_id} className="flex justify-between">
                  <span>{r.full_name}</span>
                  <span className="text-muted-foreground">{r.last_event_at ? timeAgoShort(r.last_event_at) : 'Never logged in'}</span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function timeAgoShort(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  const m = Math.floor(ms / 60_000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  const d = Math.floor(h / 24);
  return `${d}d`;
}

function Heatmap({ days }: { days: number[] }) {
  const max = Math.max(1, ...days);
  return (
    <div className="mt-1.5 flex items-end gap-0.5" title="Last 14 days of activity">
      {days.map((c, i) => {
        const intensity = c === 0 ? 0 : Math.max(0.18, c / max);
        return (
          <div
            key={i}
            className="h-3 w-2 rounded-sm"
            style={{ backgroundColor: c === 0 ? 'hsl(var(--muted))' : `hsl(var(--primary) / ${intensity})` }}
          />
        );
      })}
    </div>
  );
}

function FunnelBars({ funnel }: { funnel: FunnelTotals }) {
  const steps = [
    { label: 'Invited', value: funnel.invited },
    { label: 'Signed in', value: funnel.signed_in },
    { label: 'Created 1st deal', value: funnel.first_deal },
    { label: 'Logged 1st activity', value: funnel.first_activity },
    { label: 'Set 1st next step', value: funnel.first_next_step },
    { label: 'Push enabled', value: funnel.push_on },
  ];
  const max = Math.max(1, funnel.invited);
  return (
    <div className="space-y-2">
      {steps.map(s => {
        const pct = Math.round((s.value / max) * 100);
        return (
          <div key={s.label} className="flex items-center gap-3 text-xs">
            <span className="w-40 shrink-0 text-muted-foreground">{s.label}</span>
            <div className="flex-1 h-2 rounded-full bg-muted overflow-hidden">
              <div className="h-full bg-primary" style={{ width: `${pct}%` }} />
            </div>
            <span className="w-16 text-right font-medium">{s.value} <span className="text-muted-foreground">({pct}%)</span></span>
          </div>
        );
      })}
    </div>
  );
}
